import { Modal, Button } from 'antd';
import { CheckCircleOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import PropTypes from 'prop-types';

const ExtensionSuccessModal = ({ open, onClose, extensionData }) => {
  if (!extensionData) return null;

  // Format extension period display
  const getExtensionPeriodText = () => {
    const weeks = extensionData.extensionWeeks || 0;
    const days = extensionData.extensionDays || 0;
    
    if (weeks > 0 && days > 0) {
      return `${weeks} week${weeks > 1 ? 's' : ''} and ${days} day${days > 1 ? 's' : ''}`;
    } else if (weeks > 0) {
      return `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    return 'your rental period';
  };

  // Create the main success message
  const getSuccessMessage = () => {
    const hasValidDates = extensionData.originalDate && 
                         extensionData.newDate && 
                         extensionData.originalDate !== 'original date' && 
                         extensionData.newDate !== 'new date';

    if (hasValidDates) {
      return (
        <>
        <p>Your rental period has been successfully extended.</p>
          <p>
            Your pickup date has been changed from
            <br />
            <strong className="extension-date-highlight">
              {extensionData.originalDate}
            </strong>{' '}
            to{' '}
            <strong className="extension-date-highlight">
              {extensionData.newDate}
            </strong>.
          </p>
        </>
      );
    } else {
      return (
        <>
          Your rental period has been successfully extended for{' '}
          <strong>{getExtensionPeriodText()}</strong>. 
          Your updated pickup date will be reflected in your order details.
        </>
      );
    }
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      centered
      width={500}
      className="extension-success-modal"
    >
      <div className="extension-success-container">
        {/* Header with close button */}
        <div className="extension-success-header">
          <div className="extension-success-icon">
            <CheckCircleOutlined className="success-check-icon" />
          </div>
          <Button
            type="text"
            icon={<CloseOutlined />}
            className="extension-success-close"
            onClick={onClose}
          />
        </div>

        {/* Success message */}
        <div className="extension-success-content">
          <h3 className="extension-success-title">Extension Successful!</h3>
          <p className="extension-success-message">
            {getSuccessMessage()}
          </p>
          
          <div className="extension-details">
            {/* Show extension period */}
            {(extensionData.extensionWeeks > 0 || extensionData.extensionDays > 0) && (
              <div className="extension-detail-item">
                <span className="extension-detail-label">Extension Period: </span>
                <span className="extension-detail-value">{getExtensionPeriodText()}</span>
              </div>
            )}
            
            {extensionData.amount && (
              <div className="extension-detail-item">
                <span className="extension-detail-label">Amount Paid: </span>
                <span className="extension-detail-value">${extensionData.amount}</span>
              </div>
            )}
            
            {extensionData.orderId && (
              <div className="extension-detail-item">
                <span className="extension-detail-label">Order ID: </span>
                <span className="extension-detail-value">{extensionData.orderId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer button */}
        <div className="extension-success-footer">
          <Button
            type="primary"
            size="large"
            onClick={onClose}
            className="extension-success-button"
          >
            Okay!
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ExtensionSuccessModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  extensionData: PropTypes.shape({
    originalDate: PropTypes.string,
    newDate: PropTypes.string,
    extensionWeeks: PropTypes.number,
    extensionDays: PropTypes.number,  
    extensionType: PropTypes.string, 
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orderId: PropTypes.string
  })
};

export default ExtensionSuccessModal;